import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Check, RefreshCw } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { env } from "@/lib/env";
import { getParticipantTelegramStatus } from "@/lib/api/participants";

interface Props {
  participantId: string;
}

/**
 * "Connect Telegram" block for a meetup participant.
 *
 * Two states:
 *   - Not linked → big button "Connect Telegram" that opens the bot deep
 *     link `https://t.me/<bot>?start=<participant_id>`. Telegram opens
 *     with "Start" pre-filled, the user taps it, the bot saves chat_id.
 *   - Linked → quiet "Connected ✓" with a refresh hint and an unlink
 *     option (not yet wired — comes with the preferences screen).
 *
 * We poll the link status every 4 seconds while the user is on this page
 * and hasn't yet connected — that's how the UI flips to "Connected ✓"
 * without needing the user to refresh after they tap "Start" in Telegram.
 */
export function TelegramConnectBlock({ participantId }: Props) {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);

  const { data: status } = useQuery({
    queryKey: ["telegram-status", participantId],
    queryFn: () => getParticipantTelegramStatus(participantId),
    enabled: Boolean(participantId),
    // Poll while the user is on the page and not yet linked — flip the UI
    // as soon as the bot has registered the chat. Once linked, stop.
    refetchInterval: (q) => (q.state.data?.isLinked ? false : opened ? 4000 : false),
  });

  // Refetch immediately when the tab regains focus — typical case is the
  // user came back from Telegram with chat just linked.
  useEffect(() => {
    function onFocus() {
      void queryClient.invalidateQueries({ queryKey: ["telegram-status", participantId] });
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [participantId, queryClient]);

  const deepLink = `https://t.me/${env.VITE_TELEGRAM_BOT_USERNAME}?start=${participantId}`;

  const isLinked = status?.isLinked ?? false;

  return (
    <Card padding="md" className="mt-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#dbeafe] text-[#2563eb]">
          <Send className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
            Telegram
          </p>
          <p className="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
            {isLinked ? "You'll get a ping for new matches" : "Get notified about new matches"}
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-[#6c757d]">
            {isLinked
              ? "Connected. We'll write to you in Telegram when someone you liked likes you back."
              : "Connect Telegram so we can message you the moment you have a new match — works even when the browser is closed."}
          </p>
        </div>
      </div>

      <div className="mt-4">
        {isLinked ? (
          <div className="inline-flex h-10 items-center gap-2 rounded-[10px] bg-[#dcfce7] px-4 text-[13px] font-semibold text-[#15803d]">
            <Check className="h-4 w-4" /> Connected
          </div>
        ) : (
          <>
            <a
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpened(true)}
              className="inline-flex h-11 items-center gap-2 rounded-[12px] bg-[#2563eb] px-5 text-[14px] font-bold text-white no-underline hover:bg-[#1d4ed8]"
            >
              <Send className="h-4 w-4" />
              Connect Telegram
            </a>
            {opened ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-[#6c757d]">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Tap "Start" in Telegram — this'll update once you do.
              </p>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
